import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Badge, Button, Col, Row, Spinner } from 'reactstrap';
import apiBase, { API_PROXY_TARGET } from '../../apiConfig';
import { getAuthHeaders } from '../../helpers/utils';

const BASE_ENDPOINT = `${apiBase}/p-admin/pasport.php`;
const STUDENT_ENDPOINT = `${BASE_ENDPOINT}?action=student`;
const BACKEND_ROOT = API_PROXY_TARGET.replace(/\/api\/?$/i, '');
const DEFAULT_TESTS = {
  temperament: 'Qiymatlanmagan',
  kommunikativ: 'Qiymatlanmagan',
  motivatsiya: 'Qiymatlanmagan',
  teylor: 'Qiymatlanmagan',
  nevratsiya: 'Qiymatlanmagan',
  dipressiya: 'Qiymatlanmagan',
  suitsit: 'Qiymatlanmagan',
  kasb: 'Qiymatlanmagan'
};
const PROFILE_FIELDS = [
  { key: 'oila', type: 'number' },
  { key: 'bitirgan_nomi', type: 'text' },
  { key: 'ota_ona_manzil', type: 'text' },
  { key: 'otasi_fio', type: 'text' },
  { key: 'onasi_fio', type: 'text' },
  { key: 'lavozim', type: 'text' },
  { key: 'harbiy_qism', type: 'text' },
  { key: 'yashash_manzil', type: 'text' },
  { key: 'shakl', type: 'number' },
  { key: 'xarakter', type: 'number' },
  { key: 'rang', type: 'text' },
  { key: 'salomatlik', type: 'text' },
  { key: 'ozlashtirish', type: 'text' },
  { key: 'munosabat', type: 'text' },
  { key: 'kursdosh', type: 'text' },
  { key: 'izoh', type: 'text' }
];
const TEST_CARDS = [
  { key: 'temperament', label: 'Temperament' },
  { key: 'kommunikativ', label: 'Kommunikativ' },
  { key: 'motivatsiya', label: 'Motivatsiya' },
  { key: 'teylor', label: 'Bezovtalanish' },
  { key: 'nevratsiya', label: 'Nevrasteniya' },
  { key: 'dipressiya', label: 'Depressiya' },
  { key: 'suitsit', label: 'Suitsid testi' },
  { key: 'kasb', label: 'Kasb testi' }
];
const SHAPE_META = {
  '1': { label: '1-shakl', asset: '/student-assets/rasm/4.jpg' },
  '2': { label: '2-shakl', asset: '/student-assets/rasm/3.jpg' },
  '3': { label: '3-shakl', asset: '/student-assets/rasm/aylana.jpg' },
  '4': { label: '4-shakl', asset: '/student-assets/rasm/z.jpg' },
  '5': { label: '5-shakl', asset: '/student-assets/rasm/tort.jpg' }
};

const readTalabaId = search => {
  const params = new URLSearchParams(search || '');
  return (params.get('talaba_id') || '').trim();
};

const extractErrorMessage = (error, fallback) =>
  (error.response && error.response.data && error.response.data.message) || error.message || fallback;

const toBackendUrl = path => {
  if (!path) {
    return '';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (path.startsWith('/')) {
    return `${BACKEND_ROOT}${path}`;
  }

  return `${BACKEND_ROOT}/${path.replace(/^\/+/, '')}`;
};

const toPreviewUrl = talabaId => {
  const query = `?talaba_id=${encodeURIComponent(String(talabaId || ''))}`;

  if (typeof window === 'undefined') {
    return `/psixologik-pasport/view${query}`;
  }

  return `${window.location.origin}/psixologik-pasport/view${query}`;
};

const joinReadable = values => {
  const filtered = values.filter(value => String(value || '').trim() !== '');
  return filtered.length > 0 ? filtered.join(', ') : "Ko'rsatilmagan";
};

const familyStatusLabel = value => {
  if (Number(value) === 1) {
    return 'Uylanmagan / turmushga chiqmagan';
  }

  if (Number(value) === 2) {
    return 'Uylangan / turmushga chiqqan';
  }

  return "Ko'rsatilmagan";
};

const characterLabel = value => {
  const labels = {
    1: "Og'ir",
    2: "Sho'x",
    3: 'Qiziqqon',
    4: 'Quvnoq',
    5: 'Qaysar'
  };

  return labels[Number(value)] || "Ko'rsatilmagan";
};

const completionMeta = student => {
  const total = PROFILE_FIELDS.length;
  const filled = PROFILE_FIELDS.reduce((count, field) => {
    const value = student ? student[field.key] : '';

    if (field.type === 'number') {
      return count + (Number(value) > 0 ? 1 : 0);
    }

    return count + (String(value || '').trim() !== '' ? 1 : 0);
  }, 0);

  return {
    total,
    filled,
    percent: total > 0 ? Math.round((filled / total) * 100) : 0
  };
};

const PassportPreviewPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [student, setStudent] = useState(null);
  const [tests, setTests] = useState(DEFAULT_TESTS);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const talabaId = readTalabaId(location.search);
  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  useEffect(() => {
    let active = true;

    const loadStudent = async () => {
      if (!talabaId) {
        setLoading(false);
        setErrorMessage('Talaba ID yuborilmagan.');
        return;
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const response = await axios.get(STUDENT_ENDPOINT, {
          ...requestConfig,
          params: {
            talaba_id: talabaId
          }
        });

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          const payload = response.data.data || {};
          setStudent(payload.student || null);
          setTests(payload.tests || DEFAULT_TESTS);
          return;
        }

        throw new Error((response.data && response.data.message) || 'Pasport ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        setErrorMessage(extractErrorMessage(error, 'Pasport ma`lumotlarini yuklashda xatolik yuz berdi.'));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadStudent();

    return () => {
      active = false;
    };
  }, [requestConfig, talabaId]);

  useEffect(() => {
    let active = true;

    const generateQr = async () => {
      if (!student || !student.talaba_id) {
        setQrCodeUrl('');
        return;
      }

      try {
        const url = toPreviewUrl(student.talaba_id);
        const dataUrl = await QRCode.toDataURL(url, {
          margin: 1,
          width: 220,
          color: {
            dark: '#0b3c8a',
            light: '#0000'
          }
        });

        if (active) {
          setQrCodeUrl(dataUrl);
        }
      } catch (error) {
        if (active) {
          setQrCodeUrl('');
        }
      }
    };

    generateQr();

    return () => {
      active = false;
    };
  }, [student]);

  const completion = useMemo(() => completionMeta(student), [student]);
  const previewUrl = useMemo(() => (student ? toPreviewUrl(student.talaba_id) : toPreviewUrl(talabaId)), [student, talabaId]);
  const shape = student ? SHAPE_META[String(student.shakl || '')] || null : null;
  const shapeAsset = shape ? toBackendUrl(shape.asset) : '';
  const imageUrl = student ? toBackendUrl(student.image_url || student.image || '/rasm/talaba/rasm-mavjud-emas.jpg') : '';
  const fullName = student ? student.full_name : '';
  const groupLabel = student ? student.group || student.group_code || "Guruh ko'rsatilmagan" : '';
  const facultyLabel = student ? student.faculty || "Fakultet ko'rsatilmagan" : '';
  const addressLabel = student
    ? joinReadable([student.republic, student.region, student.district, student.address_raw || student.address])
    : "Ko'rsatilmagan";

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(previewUrl);
        return;
      }
    } catch (error) {
      // Fallback below.
    }

    window.prompt('Havolani nusxalash uchun belgilang:', previewUrl);
  };

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.push('/psixologik-pasport');
  };

  return (
    <div className="psi-passport-preview">
      <div className="psi-passport-preview__bg" />
      <div className="psi-passport-preview__shell">
        <div className="psi-passport-preview__topbar">
          <div className="psi-passport-preview__intro">
            <div className="psi-passport-preview__eyebrow">Psixologik pasport preview</div>
            <h1>Talaba pasporti</h1>
            <p>To‘liq React preview, QR orqali qayta chaqiriladi va chop etish uchun soddalashtirilgan.</p>
          </div>

          <div className="psi-passport-preview__actions">
            <Button color="light" className="psi-passport-preview__action" onClick={handleBack}>
              Ortga
            </Button>
            <Button color="light" className="psi-passport-preview__action" onClick={handleCopy}>
              Linkni nusxalash
            </Button>
            <Button color="primary" className="psi-passport-preview__action psi-passport-preview__action--primary" onClick={() => window.print()}>
              Chop etish
            </Button>
          </div>
        </div>

        {errorMessage ? (
          <Alert color="danger" className="mb-4">
            {errorMessage}
          </Alert>
        ) : null}

        {loading ? (
          <div className="psi-passport-preview__loading">
            <Spinner color="primary" />
            <span>Pasport ma`lumotlari yuklanmoqda...</span>
          </div>
        ) : null}

        {!loading && student ? (
          <>
            <div className="psi-passport-preview__layout">
              <div className="psi-passport-preview__main">
                <section className="psi-passport-preview__hero-main">
                  <div className="psi-passport-preview__chips">
                    <Badge pill className="psi-passport-preview__chip">
                      Talaba ID: {student.talaba_id}
                    </Badge>
                    <Badge pill className="psi-passport-preview__chip">
                      {groupLabel}
                    </Badge>
                    <Badge pill className="psi-passport-preview__chip psi-passport-preview__chip--soft">
                      {student.course ? `${student.course}-kurs` : "Kurs ko'rsatilmagan"}
                    </Badge>
                  </div>

                  <h2 className="psi-passport-preview__hero-title">{fullName}</h2>
                  <p className="psi-passport-preview__hero-text">
                    {facultyLabel} bo‘limidagi talaba uchun shakllantirilgan ijtimoiy-psixologik profil. Ko‘rinish ixcham React pasport formatida tartiblangan.
                  </p>

                  <div className="psi-passport-preview__metrics">
                    <div className="psi-passport-preview__metric">
                      <strong>{completion.percent}%</strong>
                      <span>To‘ldirilish darajasi</span>
                    </div>
                    <div className="psi-passport-preview__metric">
                      <strong>
                        {completion.filled}/{completion.total}
                      </strong>
                      <span>Faol maydonlar</span>
                    </div>
                    <div className="psi-passport-preview__metric">
                      <strong>{student.gender || '—'}</strong>
                      <span>Jinsi</span>
                    </div>
                    <div className="psi-passport-preview__metric">
                      <strong>{student.phone || '—'}</strong>
                      <span>Aloqa raqami</span>
                    </div>
                  </div>
                </section>

                <section className="psi-passport-preview__card">
                  <div className="psi-passport-preview__section-tag">Asosiy profil</div>
                  <h3>Talabaning bazaviy ma`lumotlari</h3>
                  <div className="psi-passport-preview__info-grid">
                    <div className="psi-passport-preview__info">
                      <span>F.I.Sh</span>
                      <strong>{fullName}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Fakultet / Guruh</span>
                      <strong>{facultyLabel} / {groupLabel}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Tug`ilgan sana</span>
                      <strong>{student.birth_date || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Doimiy manzil</span>
                      <strong>{addressLabel}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Pasport</span>
                      <strong>{student.passport || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>JSHSHIR</span>
                      <strong>{student.jshshir || "Ko'rsatilmagan"}</strong>
                    </div>
                  </div>
                </section>

                <section className="psi-passport-preview__card">
                  <div className="psi-passport-preview__section-tag">Testlar</div>
                  <h3>Psixologik ko‘rsatkichlar</h3>
                  <div className="psi-passport-preview__test-grid">
                    {TEST_CARDS.map(test => (
                      <div key={test.key} className="psi-passport-preview__test">
                        <span>{test.label}</span>
                        <strong>{tests[test.key] || 'Qiymatlanmagan'}</strong>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="psi-passport-preview__card">
                  <div className="psi-passport-preview__section-tag">Ijtimoiy blok</div>
                  <h3>Oilaviy va ijtimoiy ma`lumotlar</h3>
                  <div className="psi-passport-preview__info-grid">
                    <div className="psi-passport-preview__info">
                      <span>Oilaviy ahvoli</span>
                      <strong>{familyStatusLabel(student.oila)}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Institutgacha tamomlagan o‘quv yurti</span>
                      <strong>{student.bitirgan_nomi || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Otasi F.I.Sh / telefon</span>
                      <strong>{student.otasi_fio || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Onasi F.I.Sh / telefon</span>
                      <strong>{student.onasi_fio || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Harbiy xizmat ma`lumoti</span>
                      <strong>{student.harbiy_qism || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Yashash sharoiti</span>
                      <strong>{student.yashash_manzil || "Ko'rsatilmagan"}</strong>
                    </div>
                  </div>
                  <div className="psi-passport-preview__note">
                    {student.ota_ona_manzil || "Oila a`zolari haqidagi izoh mavjud emas."}
                  </div>
                </section>

                <section className="psi-passport-preview__card">
                  <div className="psi-passport-preview__section-tag">Shaxsiy tavsif</div>
                  <h3>Shaxsiy rivojlanish va kuzatuv</h3>
                  <div className="psi-passport-preview__info-grid">
                    <div className="psi-passport-preview__info">
                      <span>Xarakter</span>
                      <strong>{characterLabel(student.xarakter)}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Yoqtirgan rang</span>
                      <strong>{student.rang || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Salomatligi haqida ma`lumot</span>
                      <strong>{student.salomatlik || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Fanlarni o`zlashtirishi</span>
                      <strong>{student.ozlashtirish || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Oilaviy munosabatlar</span>
                      <strong>{student.munosabat || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__info">
                      <span>Kursdoshlari bilan munosabati</span>
                      <strong>{student.kursdosh || "Ko'rsatilmagan"}</strong>
                    </div>
                  </div>
                </section>

                <section className="psi-passport-preview__card">
                  <div className="psi-passport-preview__section-tag">Mutaxassis izohi</div>
                  <h3>Psixolog xulosasi</h3>
                  <div className="psi-passport-preview__note psi-passport-preview__note--large">
                    {student.izoh || 'Mutaxassis izohi hozircha kiritilmagan.'}
                  </div>
                </section>
              </div>

              <aside className="psi-passport-preview__aside">
                <section className="psi-passport-preview__hero-side">
                  <img
                    src={imageUrl}
                    alt={fullName}
                    className="psi-passport-preview__photo"
                    onError={event => {
                      // eslint-disable-next-line no-param-reassign
                      event.target.src = toBackendUrl('/rasm/talaba/rasm-mavjud-emas.jpg');
                    }}
                  />

                  <div className="psi-passport-preview__progress">
                    <div className="psi-passport-preview__progress-head">
                      <span>Pasport qamrovi</span>
                      <strong>{completion.percent}%</strong>
                    </div>
                    <div className="psi-passport-preview__progress-track">
                      <div className="psi-passport-preview__progress-bar" style={{ width: `${completion.percent}%` }} />
                    </div>
                  </div>

                  <div className="psi-passport-preview__qr-card">
                    <strong>QR orqali ochish</strong>
                    <span>Mobil qurilmada shu React preview sahifasini qayta ochadi.</span>
                    <div className="psi-passport-preview__qr-box">
                      {qrCodeUrl ? <img src={qrCodeUrl} alt="Passport QR code" /> : <div className="psi-passport-preview__qr-placeholder">QR</div>}
                    </div>
                  </div>
                </section>

                <section className="psi-passport-preview__card">
                  <div className="psi-passport-preview__section-tag">Shakl tanlovi</div>
                  <h3>Vizual tanlov</h3>
                  <div className="psi-passport-preview__shape-frame">
                    {shape ? (
                      <img src={shapeAsset} alt={shape.label} />
                    ) : (
                      <div className="psi-passport-preview__shape-empty">?</div>
                    )}
                  </div>
                  <div className="psi-passport-preview__mini">
                    <span>Tanlangan shakl</span>
                    <strong>{shape ? shape.label : "Ko'rsatilmagan"}</strong>
                  </div>
                </section>

                <section className="psi-passport-preview__card">
                  <div className="psi-passport-preview__section-tag">Qo‘shimcha</div>
                  <h3>Qisqa ko‘rsatkichlar</h3>
                  <div className="psi-passport-preview__stack">
                    <div className="psi-passport-preview__mini">
                      <span>HEMIS ID</span>
                      <strong>{student.hemis_id || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__mini">
                      <span>Guruh kodi</span>
                      <strong>{student.group_code || "Ko'rsatilmagan"}</strong>
                    </div>
                    <div className="psi-passport-preview__mini psi-passport-preview__mini--print-hide">
                      <span>Preview havola</span>
                      <strong className="psi-passport-preview__breakable">{previewUrl}</strong>
                    </div>
                  </div>
                </section>
              </aside>
            </div>

            <Row className="psi-passport-preview__footer">
              <Col md="8">
                QR kod shu React preview’ga yo‘naltiriladi. Chop etish rejimida fon soddalashtiriladi va asosiy kontent toza ko‘rinishda qoladi.
              </Col>
              <Col md="4" className="text-md-right">
                Yangilanish: {new Date().toLocaleString('uz-UZ')}
              </Col>
            </Row>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PassportPreviewPage;
